import {
  Content,
  Header,
  HeaderLabel,
  Page,
  Table,
  TableColumn,
  Link as BackstageLink,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { catalogApiRef, EntityRefLink } from '@backstage/plugin-catalog-react';
import { techInsightsApiRef } from '@backstage-community/plugin-tech-insights-react';
import { ScorecardsList } from '@backstage-community/plugin-tech-insights';
import {
  Box,
  Button,
  Badge,
  Checkbox,
  Chip,
  Drawer,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  Typography,
  makeStyles,
} from '@material-ui/core';
import FilterListIcon from '@material-ui/icons/FilterList';
import { useEffect, useMemo, useState } from 'react';

// ── Catalog URL helpers (same as DefectDojo dashboard) ────────────────────────

function systemCatalogUrl(system: string): string {
  const parts = system.split('/');
  const [namespace, name] = parts.length === 2 ? parts : ['default', parts[0]];
  return `/catalog/${namespace}/system/${name}`;
}

function systemLabel(system: string): string {
  return system.includes('/') ? system.split('/').pop()! : system;
}

function ownerCatalogUrl(owner: string): string {
  const colonIdx = owner.indexOf(':');
  let kind = 'group';
  let rest = owner;
  if (colonIdx !== -1) {
    kind = owner.substring(0, colonIdx);
    rest = owner.substring(colonIdx + 1);
  }
  const slashIdx = rest.indexOf('/');
  const [namespace, name] =
    slashIdx !== -1
      ? [rest.substring(0, slashIdx), rest.substring(slashIdx + 1)]
      : ['default', rest];
  return `/catalog/${namespace}/${kind}/${name}`;
}

function ownerLabel(owner: string): string {
  const afterColon = owner.includes(':') ? owner.split(':')[1] : owner;
  return afterColon.includes('/') ? afterColon.split('/').pop()! : afterColon;
}

// ── Row type ──────────────────────────────────────────────────────────────────

/** Emitted by the repositoryMaintainersProcessor (see docs/custom-catalog-backend-modules.md). */
const MAINTAINED_BY_RELATION = 'maintainedBy';

interface TechInsightsRow {
  entityRef: string;
  system: string | null;
  owner: string | null;
  tags: string | null;
  /** Display names of the entity's maintainers, sorted. */
  maintainers: string[];
  results: any[];
}

type StatusFilter = 'all' | 'failures' | 'passed';

// ── Sort helpers ──────────────────────────────────────────────────────────────

const sortByFailed = (a: TechInsightsRow, b: TechInsightsRow) => {
  const aFailed = a.results.filter((r: any) => r.result === false).length;
  const bFailed = b.results.filter((r: any) => r.result === false).length;
  if (aFailed !== bFailed) return bFailed - aFailed;
  return b.results.length - a.results.length;
};

// ── Filter panel styles ───────────────────────────────────────────────────────

const useFilterPanelStyles = makeStyles(theme => ({
  drawerPaper: {
    width: 260,
    padding: theme.spacing(3, 2.5),
    top: 0,
    height: '100%',
    overflowY: 'auto',
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
  },
  drawerTitle: {
    fontSize: '1.25rem',
    fontWeight: 700,
  },
  filterLabel: {
    fontSize: '0.875rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.75),
    display: 'block',
  },
  filterItem: {
    marginBottom: theme.spacing(2.5),
  },
}));

// ── Page component ────────────────────────────────────────────────────────────

function MultiSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  const classes = useFilterPanelStyles();
  return (
    <div className={classes.filterItem}>
      <Typography component="span" className={classes.filterLabel}>{label}</Typography>
      <FormControl variant="outlined" size="small" fullWidth>
        <Select
          multiple
          displayEmpty
          value={value}
          onChange={e => onChange(e.target.value as string[])}
          renderValue={selected =>
            (selected as string[]).length === 0
              ? <span style={{ opacity: 0.45 }}>All</span>
              : (selected as string[]).join(', ')
          }
        >
          {options.map(opt => (
            <MenuItem key={opt} value={opt} dense>
              <Checkbox checked={value.includes(opt)} size="small" />
              <ListItemText primary={opt} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
}

export function TechInsightsDashboardPage() {
  const techInsightsApi = useApi(techInsightsApiRef);
  const catalogApi = useApi(catalogApiRef);

  const [rows, setRows] = useState<TechInsightsRow[]>([]);
  const [allChecks, setAllChecks] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // ── Filter state ────────────────────────────────────────────────────────────
  const classes = useFilterPanelStyles();
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);
  const [selectedOwners, setSelectedOwners]   = useState<string[]>([]);
  const [selectedTags, setSelectedTags]       = useState<string[]>([]);
  const [selectedMaintainers, setSelectedMaintainers] = useState<string[]>([]);
  const [selectedCheckIds, setSelectedCheckIds] = useState<string[]>([]);
  const [statusFilter, setStatusFilter]         = useState<StatusFilter>('all');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [checkResults, { items: entities }, { items: users }, checks] = await Promise.all([
          techInsightsApi.runBulkChecks([]),
          catalogApi.getEntities({
            filter: {
              kind: 'Component',
              'spec.type': ['service', 'sidecar']
            },
            fields: [
              'kind',
              'metadata.name',
              'metadata.namespace',
              'metadata.tags',
              'spec.system',
              'spec.owner',
              'spec.type',
              'relations',
            ],
          }),
          catalogApi.getEntities({
            filter: { kind: 'User' },
            fields: ['metadata.name', 'metadata.namespace', 'spec.profile'],
          }),
          techInsightsApi.getAllChecks(),
        ]);

        const displayNames = new Map<string, string>();
        for (const user of users) {
          const namespace = user.metadata.namespace ?? 'default';
          displayNames.set(
            `user:${namespace}/${user.metadata.name}`.toLowerCase(),
            (user.spec as any)?.profile?.displayName ?? user.metadata.name,
          );
        }

        const entityMap = new Map(entities.map(e => [stringifyEntityRef(e), e]));

        const data: TechInsightsRow[] = checkResults
          .filter(r => r.results.length > 0 && entityMap.has(r.entity))
          .map(r => {
            const entity = entityMap.get(r.entity);
            return {
              entityRef: r.entity,
              system: (entity as any)?.spec?.system ?? null,
              owner: (entity as any)?.spec?.owner ?? null,
              tags: entity?.metadata?.tags?.length
                ? entity.metadata.tags.join(', ')
                : null,
              maintainers: (entity?.relations ?? [])
                .filter(relation => relation.type === MAINTAINED_BY_RELATION)
                .map(relation => {
                  const ref = relation.targetRef.toLowerCase();
                  // A relation can point at a User the catalog hasn't ingested
                  // (a default reviewer who left); fall back to the ref's
                  // name part rather than dropping them silently.
                  return displayNames.get(ref) ?? ref.split('/').pop()!;
                })
                .sort((a, b) => a.localeCompare(b)),
              results: r.results,
            };
          });

        if (!cancelled) {
          setRows(data);
          setAllChecks(checks);
        }
      } catch (e: any) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [techInsightsApi, catalogApi]);

  // ── Unique option lists ─────────────────────────────────────────────────────
  const systemOptions = useMemo(
    () => [...new Set(rows.map(r => r.system).filter(Boolean) as string[])].sort(),
    [rows],
  );
  const ownerOptions = useMemo(
    () => [...new Set(rows.map(r => r.owner).filter(Boolean) as string[])].sort(),
    [rows],
  );
  const tagOptions = useMemo(
    () => [...new Set(rows.flatMap(r => (r.tags ? r.tags.split(', ') : [])))].sort(),
    [rows],
  );
  const checkOptions = useMemo(
    () => allChecks.map(c => c.name).sort(),
    [allChecks],
  );
  const maintainerOptions = useMemo(
    () => [...new Set(rows.flatMap(r => r.maintainers))].sort((a, b) => a.localeCompare(b)),
    [rows],
  );
  // Map check name → id for filtering
  const checkNameToId = useMemo(
    () => new Map(allChecks.map(c => [c.name, c.id])),
    [allChecks],
  );

  // ── Filtered rows ───────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const selectedIds = selectedCheckIds.map(name => checkNameToId.get(name)).filter(Boolean) as string[];

    return rows
      .map(r => {
        // Narrow results to selected checks (if any)
        const visibleResults = selectedIds.length
          ? r.results.filter((res: any) => selectedIds.includes(res.check?.id))
          : r.results;
        return { ...r, results: visibleResults };
      })
      .filter(r => {
        if (selectedSystems.length && !selectedSystems.includes(r.system ?? '')) return false;
        if (selectedOwners.length  && !selectedOwners.includes(r.owner ?? ''))   return false;
        if (selectedTags.length) {
          const rowTags = r.tags ? r.tags.split(', ') : [];
          if (!selectedTags.some(t => rowTags.includes(t))) return false;
        }
        if (selectedMaintainers.length && !selectedMaintainers.some(m => r.maintainers.includes(m))) return false;
        // Check filter: only keep rows that have at least one visible result
        if (selectedIds.length && r.results.length === 0) return false;
        // Status filter
        if (statusFilter === 'failures' && !r.results.some((res: any) => res.result === false)) return false;
        if (statusFilter === 'passed'   && !r.results.every((res: any) => res.result === true)) return false;
        return true;
      });
  }, [rows, selectedSystems, selectedOwners, selectedTags, selectedMaintainers, selectedCheckIds, statusFilter, checkNameToId]);

  const activeFilterCount =
    selectedSystems.length + selectedOwners.length + selectedTags.length +
    selectedMaintainers.length + selectedCheckIds.length + (statusFilter !== 'all' ? 1 : 0);

  const columns: TableColumn<TechInsightsRow>[] = [
    {
      title: 'Entity',
      field: 'entityRef',
      defaultSort: 'asc',
      searchable: true,
      render: r => <EntityRefLink entityRef={r.entityRef} />,
    },
    {
      title: 'System',
      field: 'system',
      searchable: false,
      customSort: (a, b) => (a.system ?? '').localeCompare(b.system ?? ''),
      render: r =>
        r.system ? (
          <BackstageLink to={systemCatalogUrl(r.system)}>
            {systemLabel(r.system)}
          </BackstageLink>
        ) : (
          <span style={{ color: '#9e9e9e' }}>—</span>
        ),
    },
    {
      title: 'Owner',
      field: 'owner',
      searchable: false,
      customSort: (a, b) => (a.owner ?? '').localeCompare(b.owner ?? ''),
      render: r =>
        r.owner ? (
          <BackstageLink to={ownerCatalogUrl(r.owner)}>
            {ownerLabel(r.owner)}
          </BackstageLink>
        ) : (
          <span style={{ color: '#9e9e9e' }}>—</span>
        ),
    },
    {
      title: 'Tags',
      field: 'tags',
      searchable: false,
      customSort: (a, b) => (a.tags ?? '').localeCompare(b.tags ?? ''),
      render: r =>
        r.tags ? (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {r.tags.split(', ').map(tag => (
              <Chip key={tag} label={tag} size="small" variant="outlined" />
            ))}
          </span>
        ) : (
          <span style={{ color: '#9e9e9e' }}>—</span>
        ),
    },
    {
      title: 'Results',
      field: 'results',
      defaultSort: 'desc',
      searchable: false,
      customSort: sortByFailed,
      sorting: true,
      render: r => <ScorecardsList checkResults={r.results} dense hideDescription />,
    },
  ];

  const clearFilters = () => {
    setSelectedSystems([]);
    setSelectedOwners([]);
    setSelectedTags([]);
    setSelectedMaintainers([]);
    setSelectedCheckIds([]);
    setStatusFilter('all');
  };

  return (
    <Page themeId="tool">
      <Header title="Tech Insights">
        <HeaderLabel label="Components" value={loading ? '…' : filtered.length} />
      </Header>
      <Content>
        {error && <p style={{ color: 'red' }}>{error.message}</p>}

        {/* ── Filters Drawer ── */}
        <Drawer
          anchor="left"
          open={filterPanelOpen}
          onClose={() => setFilterPanelOpen(false)}
          classes={{ paper: classes.drawerPaper }}
        >
          <div className={classes.drawerHeader}>
            <Typography className={classes.drawerTitle}>Filters</Typography>
          </div>
          <MultiSelect
            label="System"
            options={systemOptions}
            value={selectedSystems}
            onChange={setSelectedSystems}
          />
          <MultiSelect
            label="Owner"
            options={ownerOptions}
            value={selectedOwners}
            onChange={setSelectedOwners}
          />
          <MultiSelect
            label="Tags"
            options={tagOptions}
            value={selectedTags}
            onChange={setSelectedTags}
          />
          <MultiSelect
            label="Maintainers"
            options={maintainerOptions}
            value={selectedMaintainers}
            onChange={setSelectedMaintainers}
          />
          <MultiSelect
            label="Checks"
            options={checkOptions}
            value={selectedCheckIds}
            onChange={setSelectedCheckIds}
          />
          <div className={classes.filterItem}>
            <Typography component="span" className={classes.filterLabel}>Status</Typography>
            <FormControl variant="outlined" size="small" fullWidth>
              <Select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as StatusFilter)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="failures">Has failures</MenuItem>
                <MenuItem value="passed">All passed</MenuItem>
              </Select>
            </FormControl>
          </div>
          {activeFilterCount > 0 && (
            <Box mt={1}>
              <Chip
                label={`Clear all (${activeFilterCount})`}
                size="small"
                onDelete={clearFilters}
              />
            </Box>
          )}
        </Drawer>

        {/* ── Toolbar: Filters toggle button ── */}
        <Box display="flex" alignItems="center" mb={2}>
          <Button
            variant="text"
            size="medium"
            startIcon={
              <Badge
                color="primary"
                badgeContent={activeFilterCount}
                invisible={activeFilterCount === 0}
              >
                <FilterListIcon />
              </Badge>
            }
            onClick={() => setFilterPanelOpen(o => !o)}
            style={{ fontWeight: 700, letterSpacing: '0.08em' }}
            aria-label={
              activeFilterCount > 0
                ? `Filters (${activeFilterCount} active)`
                : 'Filters'
            }
          >
            Filters
          </Button>
        </Box>

        {/* ── Table ── */}
        <Table
          columns={columns}
          data={filtered}
          isLoading={loading}
          title={
            loading
              ? 'Loading…'
              : `${filtered.length}${activeFilterCount > 0 ? ` / ${rows.length}` : ''} component${rows.length > 1 ? 's' : ''}`
          }
          options={{
            pageSize: 20,
            pageSizeOptions: [10, 20, 50],
            search: true,
            sorting: true,
          }}
        />
      </Content>
    </Page>
  );
}
